import { getToken, updateUserPhone } from '../../../utils/api';
import { PHONE_KEY_RE, PHONE_SUBMIT_BTN_RE } from './constants';
import { forEachTawkIframe } from './dom';

const phoneFormDocs = new WeakSet();
let lastSavedTawkPhone = '';

function looksLikePhoneKey(key) {
  return PHONE_KEY_RE.test(String(key || ''));
}

function combinePhoneParts(value) {
  if (!value || typeof value !== 'object') return '';
  const number = value.number ?? value.phone ?? value.value ?? value.nationalNumber ?? value.e164;
  if (number == null || number === '') return '';
  const digits = String(number).trim();
  if (!digits) return '';
  if (digits.startsWith('+')) return digits;
  const code = String(value.code ?? value.dialCode ?? value.countryCode ?? '').trim();
  if (code) return `${code}${digits}`.replace(/\s+/g, '');
  return digits;
}

function extractPhoneFromTawkData(data) {
  if (data == null) return '';
  if (typeof data === 'string' || typeof data === 'number') return String(data).trim();
  if (Array.isArray(data)) {
    for (const item of data) {
      if (!item || typeof item !== 'object') continue;
      const label = item.label ?? item.name ?? item.key ?? item.field ?? item.type ?? '';
      if (!looksLikePhoneKey(label)) continue;
      const phone = extractPhoneFromTawkData(item.answer ?? item.value ?? item.phone ?? item);
      if (phone) return phone;
    }
    return '';
  }
  if (typeof data !== 'object') return '';
  const direct = combinePhoneParts(data);
  if (direct && looksLikePhoneKey(Object.keys(data).join(' '))) return direct;
  for (const [key, value] of Object.entries(data)) {
    if (!looksLikePhoneKey(key)) continue;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const combined = combinePhoneParts(value);
      if (combined) return combined;
    }
    const phone = extractPhoneFromTawkData(value);
    if (phone) return phone;
  }
  if (data.questions) {
    const fromQuestions = extractPhoneFromTawkData(data.questions);
    if (fromQuestions) return fromQuestions;
  }
  return '';
}

function extractPhoneFromTawkDoc(doc) {
  if (!doc?.querySelectorAll) return '';
  const inputs = doc.querySelectorAll('input, textarea');
  for (const input of inputs) {
    const type = String(input.type || '').toLowerCase();
    const hint = [
      input.name,
      input.id,
      input.placeholder,
      input.getAttribute('aria-label'),
      input.getAttribute('autocomplete'),
      input.className,
    ]
      .join(' ')
      .toLowerCase();
    if (type !== 'tel' && !PHONE_KEY_RE.test(hint)) continue;
    const value = String(input.value || '').trim();
    if (value) return value;
  }
  return '';
}

function persistTawkPhone(phone) {
  const token = getToken();
  const normalized = String(phone || '').trim();
  const digits = normalized.replace(/\D/g, '');
  if (!token || !normalized || digits.length < 10) return;
  const saveKey = `${token}:${normalized}`;
  if (saveKey === lastSavedTawkPhone) return;
  lastSavedTawkPhone = saveKey;
  updateUserPhone(normalized).then((result) => {
    if (!result?.ok) lastSavedTawkPhone = '';
  });
}

function persistTawkPhoneFromData(data) {
  persistTawkPhone(extractPhoneFromTawkData(data));
}

function persistTawkPhoneFromDocs() {
  forEachTawkIframe((iframe) => {
    try {
      persistTawkPhone(extractPhoneFromTawkDoc(iframe.contentDocument));
    } catch {
      // Cross-origin iframe — expected.
    }
  });
}

function persistFromTawkEvent(api, data) {
  if (data) persistTawkPhoneFromData(data);
  persistTawkPhoneFromDocs();
  persistTawkPhone(api.visitor?.phone);
}

export function bindTawkPhoneCapture(api) {
  if (!api || api.__richuelPhoneCapture) return;
  api.__richuelPhoneCapture = true;

  const previousPrechat = api.onPrechatSubmit;
  const previousOffline = api.onOfflineSubmit;
  const previousChatStarted = api.onChatStarted;

  api.onPrechatSubmit = function (data) {
    persistFromTawkEvent(api, data);
    if (typeof previousPrechat === 'function') previousPrechat(data);
  };

  api.onOfflineSubmit = function (data) {
    persistFromTawkEvent(api, data);
    if (typeof previousOffline === 'function') previousOffline(data);
  };

  api.onChatStarted = function () {
    persistFromTawkEvent(api);
    if (typeof previousChatStarted === 'function') previousChatStarted();
  };
}

export function watchTawkPhoneForm(doc) {
  if (!doc || phoneFormDocs.has(doc)) return;
  phoneFormDocs.add(doc);
  const persistFromDoc = () => persistTawkPhone(extractPhoneFromTawkDoc(doc));
  doc.addEventListener('submit', persistFromDoc, true);
  doc.addEventListener(
    'click',
    (event) => {
      const btn = event.target.closest?.('button, [role="button"], input[type="submit"]');
      if (!btn) return;
      const label = `${btn.textContent || ''} ${btn.getAttribute('aria-label') || ''} ${btn.title || ''}`;
      if (!PHONE_SUBMIT_BTN_RE.test(label)) return;
      window.setTimeout(persistFromDoc, 0);
    },
    true
  );
}
